import styled from '@emotion/styled';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import NumberFormat from 'react-number-format';

// Imported Components
import { H2, H6 } from '../text-elements/oc-h';

// Styled Components
const Container = styled.div`
  display: block;
  margin: 0 auto;
  max-width: 1170px;
  position: relative;
  width: 100%;
`;

const Table = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  color: #333;
  display: inline-block;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 0.01%;
  overflow-x: auto;
  width: 100%;

  &.four-up {
    th,
    td {
      width: 25%;
    }
  }

  &.five-up {
    th,
    td {
      width: 20%;
    }
  }

  tbody tr:nth-of-type(even) {
    background: #f8f8f8;
  }

  .pink {
    color: #d40f7d;
  }

  .strong {
    font-weight: bold;
  }
`;

const TableHeader = styled.th`
  border-bottom: 2px solid #bcbcbc;
  border-left: 1px solid #bcbcbc;
  border-top: 2px solid #bcbcbc;
  font-size: 14px;
  padding: 8px 12px;
  text-transform: none;

  &:first-of-type {
    border-left: 0;
    text-align: left;
  }
`;

const TableRow = styled.tr`
  &.divider {
    border-top: 8px solid #bcbcbc;
  }
`;

const TableData = styled.td`
  border-bottom: 1px solid #bcbcbc;
  border-left: 1px solid #bcbcbc;
  min-width: 184px;
  padding: 8px 12px;
  text-align: center;

  &:first-of-type {
    border-left: 0;
  }

  &.left {
    text-align: left;
  }
`;

const ProductName = styled.strong`
  display: inline-block;
  font-size: 16px;
  text-transform: uppercase;
  width: 100%;
`;

const Input = styled(NumberFormat, { displayType: 'input' })`
  appearance: none;
  border-radius: 0;
  border: 1px solid #585858;
  display: inline-block;
  font-size: 16px;
  padding: 14px 8px;
  position: relative;
  width: 100%;
`;

const ResultContainer = styled.div`
  display: inline-block;
  margin-right: 4%;
  min-width: 242px;
  position: relative;

  &:last-of-type {
    margin-right: 0;
  }

  h2 {
    color: #d40f7d;
  }
`;

const BrandingText = styled.p`
  font-size: 14px;
  margin-bottom: 16px;
  margin-top: 0;
`;

const StackedInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputWithLabel = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;

  input {
    flex: 0 0 66%;
    width: 66%;
  }

  span {
    margin-left: 8px;
    white-space: nowrap;
  }
`;

const StackedRates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > div {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 49px;
  }
`;

// eslint-disable-next-line no-empty-pattern
const PadpPromoCalc = ({}) => {
  const [exteriorDoors, setExteriorDoors] = useState(0);
  const [interiorDoors, setInteriorDoors] = useState(0);
  const [fiberglasPurchases, setFiberglasPurchases] = useState(55000);
  const [loosefillPurchases, setLoosefillPurchases] = useState(12000);
  const [foamularPurchases, setFoamularPurchases] = useState(10000);
  const [ezsheathPurchases, setEzsheathPurchases] = useState(5000);
  const [thermafiberPurchases, setThermafiberPurchases] = useState(3500);
  const [roofingPurchases, setRoofingPurchases] = useState(30000);
  const [lumberPurchases, setLumberPurchases] = useState(10000);
  const [tierLevel, setTierLevel] = useState('diamond');

  const tierEligibleCategoryCount = [
    exteriorDoors,
    interiorDoors,
    fiberglasPurchases,
    loosefillPurchases,
    foamularPurchases,
    ezsheathPurchases,
    thermafiberPurchases,
    roofingPurchases,
  ].filter(v => v > 0).length;

  const insulationFundingRate = {
    'not-available': 0,
    silver: 0.01,
    gold: 0.02,
    diamond: 0.03,
  };

  const ezsheathFundingRate = {
    'not-available': 0,
    silver: 0,
    gold: 0,
    diamond: 0.03,
  };

  const roofingFundingRate = {
    'not-available': 0,
    silver: 0.01,
    gold: 0.01,
    diamond: 0.01,
  };

  const lumberFundingRate = {
    'not-available': 0,
    silver: 0.01,
    gold: 0.01,
    diamond: 0.03,
  };

  const fiberglasFieldName = 'Pink Next Gen™ Fiberglas™';
  const loosefillFieldName = 'ProCat® Loosefill';
  const foamularFieldName = 'Foamular®';
  const ezsheathFieldName = 'EZSheath™';
  const thermafiberFieldName = 'Thermafiber®';
  const roofingFieldName = 'Roofing';
  const lumberFieldName = 'Lumber';

  const fundingRatePercentage = rate => `${rate * 100}%`;

  const getDoorFundingRates = () => {
    switch (tierLevel) {
      case 'silver':
        return { exterior: 1.0, interior: 0.5 };
      case 'gold':
      case 'diamond':
        return { exterior: 2.0, interior: 1.0 };
      default:
        return { exterior: 0, interior: 0 };
    }
  };

  const getExteriorDoorFunds = () => {
    const { exterior } = getDoorFundingRates();
    return exteriorDoors * exterior;
  };

  const getInteriorDoorFunds = () => {
    const { interior } = getDoorFundingRates();
    return interiorDoors * interior;
  };

  const calculateDoorsFunds = () => {
    const { exterior, interior } = getDoorFundingRates();
    return exteriorDoors * exterior + interiorDoors * interior;
  };

  const calculatePromoFunds = (value, rate = insulationFundingRate[tierLevel]) => value * rate;

  const calculateTotalFunding = () => {
    const fiberglasFunds = calculatePromoFunds(fiberglasPurchases);
    const loosefillFunds = calculatePromoFunds(loosefillPurchases);
    const foamularFunds = calculatePromoFunds(foamularPurchases);
    const ezsheathFunds = calculatePromoFunds(ezsheathPurchases, ezsheathFundingRate[tierLevel]);
    const thermafiberFunds = calculatePromoFunds(thermafiberPurchases);
    const roofingFunds = calculatePromoFunds(roofingPurchases, roofingFundingRate[tierLevel]);
    const lumberFunds = calculatePromoFunds(lumberPurchases, lumberFundingRate[tierLevel]);
    const doorsFunds = calculateDoorsFunds();

    return (
      fiberglasFunds +
      loosefillFunds +
      foamularFunds +
      ezsheathFunds +
      thermafiberFunds +
      roofingFunds +
      lumberFunds +
      doorsFunds
    );
  };

  const updateFunds = (event, categoryCallback) => {
    const value = event.floatValue ? event.floatValue : 0;
    categoryCallback(value);
  };

  const updateExteriorDoors = event => {
    updateFunds(event, setExteriorDoors);
  };

  const updateInteriorDoors = event => {
    updateFunds(event, setInteriorDoors);
  };

  const updateFiberglas = event => {
    updateFunds(event, setFiberglasPurchases);
  };

  const updateLoosefill = event => {
    updateFunds(event, setLoosefillPurchases);
  };

  const updateFoamular = event => {
    updateFunds(event, setFoamularPurchases);
  };

  const updateEzsheath = event => {
    updateFunds(event, setEzsheathPurchases);
  };

  const updateThermafiber = event => {
    updateFunds(event, setThermafiberPurchases);
  };

  const updateRoofing = event => {
    updateFunds(event, setRoofingPurchases);
  };

  const updateLumber = event => {
    updateFunds(event, setLumberPurchases);
  };

  const trackUpdate = fieldName => {
    if (typeof _satellite !== 'undefined') {
      _satellite.track('calc-engagement', {
        calcName: 'PADP Promo Funds Calculator',
        fieldUpdated: fieldName,
      });
    }
  };

  useEffect(() => {
    let tierEligibleSpendingCategories = 0;
    [
      exteriorDoors,
      interiorDoors,
      fiberglasPurchases,
      loosefillPurchases,
      foamularPurchases,
      ezsheathPurchases,
      thermafiberPurchases,
      roofingPurchases,
      // eslint-disable-next-line array-callback-return
    ].map(purchases => {
      // eslint-disable-next-line no-plusplus
      if (purchases > 0) tierEligibleSpendingCategories++;
    });

    if (tierEligibleSpendingCategories >= 4) {
      setTierLevel('diamond');
    } else if (tierEligibleSpendingCategories >= 2) {
      setTierLevel('gold');
    } else if (tierEligibleSpendingCategories === 1) {
      setTierLevel('silver');
    } else {
      setTierLevel('not-available');
    }
  }, [
    exteriorDoors,
    interiorDoors,
    fiberglasPurchases,
    loosefillPurchases,
    foamularPurchases,
    ezsheathPurchases,
    thermafiberPurchases,
    roofingPurchases,
  ]);

  return (
    <Container>
      <Table className="four-up">
        <thead>
          <TableRow>
            <TableHeader>Category</TableHeader>
            <TableHeader>Purchases</TableHeader>
            <TableHeader>Funding Rate</TableHeader>
            <TableHeader className="pink">Potential Promotional Funds</TableHeader>
          </TableRow>
        </thead>
        <tbody>
          <TableRow>
            <TableData className="left">
              <ProductName>Exterior Doors</ProductName>
            </TableData>
            <TableData>
              <InputWithLabel>
                <Input
                  allowEmptyFormatting
                  allowNegative={false}
                  decimalScale={0}
                  onBlur={() => trackUpdate('Exterior Doors')}
                  onValueChange={updateExteriorDoors}
                  thousandSeparator
                  value={exteriorDoors}
                />
                <span>Units</span>
              </InputWithLabel>
            </TableData>
            <TableData>
              {tierLevel === 'not-available' ? '-' : `$${tierLevel === 'silver' ? '1.00' : '2.00'} / unit`}
            </TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={getExteriorDoorFunds()}
              />
            </TableData>
          </TableRow>

          <TableRow>
            <TableData className="left">
              <ProductName>Interior Doors</ProductName>
            </TableData>
            <TableData>
              <InputWithLabel>
                <Input
                  allowEmptyFormatting
                  allowNegative={false}
                  decimalScale={0}
                  onBlur={() => trackUpdate('Interior Doors')}
                  onValueChange={updateInteriorDoors}
                  thousandSeparator
                  value={interiorDoors}
                />
                <span>Units</span>
              </InputWithLabel>
            </TableData>
            <TableData>
              {tierLevel === 'not-available' ? '-' : `$${tierLevel === 'silver' ? '0.50' : '1.00'} / unit`}
            </TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={getInteriorDoorFunds()}
              />
            </TableData>
          </TableRow>

          <TableRow className="divider">
            <TableData className="left">
              <ProductName>{fiberglasFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(fiberglasFieldName)}
                onValueChange={event => updateFiberglas(event)}
                prefix="$"
                thousandSeparator
                value={fiberglasPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(fiberglasPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="left">
              <ProductName>{loosefillFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(loosefillFieldName)}
                onValueChange={event => updateLoosefill(event)}
                prefix="$"
                thousandSeparator
                value={loosefillPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(loosefillPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="left">
              <ProductName>{thermafiberFieldName}</ProductName>
            </TableData>
            <TableData>
              <Input
                allowEmptyFormatting
                allowNegative={false}
                decimalScale={0}
                onBlur={() => trackUpdate(thermafiberFieldName)}
                onValueChange={event => updateThermafiber(event)}
                prefix="$"
                thousandSeparator
                value={thermafiberPurchases}
              />
            </TableData>
            <TableData>{fundingRatePercentage(insulationFundingRate[tierLevel])}</TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={calculatePromoFunds(thermafiberPurchases)}
              />
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="left">
              <ProductName>{foamularFieldName}</ProductName>
            </TableData>
            <TableData>
              <StackedInputs>
                <InputWithLabel>
                  <Input
                    allowEmptyFormatting
                    allowNegative={false}
                    decimalScale={0}
                    onBlur={() => trackUpdate(foamularFieldName)}
                    onValueChange={event => updateFoamular(event)}
                    prefix="$"
                    thousandSeparator
                    value={foamularPurchases}
                  />
                  <span>{foamularFieldName}</span>
                </InputWithLabel>
                <InputWithLabel>
                  <Input
                    allowEmptyFormatting
                    allowNegative={false}
                    decimalScale={0}
                    onBlur={() => trackUpdate(ezsheathFieldName)}
                    onValueChange={event => updateEzsheath(event)}
                    prefix="$"
                    thousandSeparator
                    value={ezsheathPurchases}
                  />
                  <span>{ezsheathFieldName}</span>
                </InputWithLabel>
              </StackedInputs>
            </TableData>
            <TableData>
              <StackedRates>
                <div>{fundingRatePercentage(insulationFundingRate[tierLevel])}</div>
                <div>{fundingRatePercentage(ezsheathFundingRate[tierLevel])}</div>
              </StackedRates>
            </TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={
                  calculatePromoFunds(foamularPurchases) +
                  calculatePromoFunds(ezsheathPurchases, ezsheathFundingRate[tierLevel])
                }
              />
            </TableData>
          </TableRow>
          <TableRow className="divider">
            <TableData className="left">
              <ProductName>{roofingFieldName}</ProductName>
            </TableData>
            <TableData>
              <StackedInputs>
                <InputWithLabel>
                  <Input
                    allowEmptyFormatting
                    allowNegative={false}
                    decimalScale={0}
                    onBlur={() => trackUpdate(roofingFieldName)}
                    onValueChange={event => updateRoofing(event)}
                    prefix="$"
                    thousandSeparator
                    value={roofingPurchases}
                  />
                  <span>{roofingFieldName}</span>
                </InputWithLabel>
                <InputWithLabel>
                  <Input
                    allowEmptyFormatting
                    allowNegative={false}
                    decimalScale={0}
                    onBlur={() => trackUpdate(lumberFieldName)}
                    onValueChange={event => updateLumber(event)}
                    prefix="$"
                    thousandSeparator
                    value={lumberPurchases}
                  />
                  <span>{lumberFieldName}</span>
                </InputWithLabel>
              </StackedInputs>
            </TableData>
            <TableData>
              <StackedRates>
                <div>{fundingRatePercentage(roofingFundingRate[tierLevel])}</div>
                <div>{fundingRatePercentage(lumberFundingRate[tierLevel])}</div>
              </StackedRates>
            </TableData>
            <TableData>
              <NumberFormat
                className="strong"
                decimalScale={0}
                displayType="text"
                prefix="$"
                thousandSeparator
                value={
                  calculatePromoFunds(roofingPurchases, roofingFundingRate[tierLevel]) +
                  calculatePromoFunds(lumberPurchases, lumberFundingRate[tierLevel])
                }
              />
            </TableData>
          </TableRow>
        </tbody>
      </Table>
      <div>
        <BrandingText>
          Owens Corning® Brands including Masonite® Doors; per unit refers to finished unit including one slab or
          multiple components/slabs/side lite/etc.
        </BrandingText>
        <BrandingText>
          <strong>*NEW PRODUCT offer for Diamond Dealers</strong> – Lumber receives an extra 2% and EZSheath™ qualifies
          for 3% funding based on the total invoice!
        </BrandingText>
      </div>
      <ResultContainer>
        <H6>Tier Level</H6>
        <H2>{_.startCase(tierLevel)}</H2>
      </ResultContainer>
      <ResultContainer>
        <H6>Total Potential Funding</H6>
        <H2>
          <NumberFormat
            decimalScale={0}
            displayType="text"
            prefix="$"
            thousandSeparator
            value={calculateTotalFunding()}
          />
        </H2>
      </ResultContainer>
      <div style={{ marginTop: 10, fontWeight: 'bold', color: '#d40f7d' }}>
        {tierLevel !== 'diamond'
          ? `YOU ARE ${4 - tierEligibleCategoryCount} ${
              4 - tierEligibleCategoryCount === 1 ? 'CATEGORY' : 'CATEGORIES'
            } AWAY FROM BECOMING A DIAMOND TIER FOR ADDITIONAL FUNDING!`
          : 'THANK YOU FOR BEING A DIAMOND DEALER!'}
      </div>
    </Container>
  );
};

export default PadpPromoCalc;
